import { Button } from '../ui';

/**
 * The app's month calendar: Monday-first weeks, today highlighted, a day
 * button per cell showing how many items fall on it plus the first few as
 * chips. Picking a day calls `onSelectDay`; the page renders that day's
 * agenda (with its actions) under the calendar. Data is the page's - the
 * calendar only lays out `events` it is given (load `visibleRange(month)`).
 */

export interface CalendarEvent {
  id: string;
  /** ISO instant; placed on its local day. */
  start: string;
  label: string;
  tone?: 'default' | 'success' | 'danger' | 'muted';
}

/** Local calendar day key, e.g. "2026-10-25". */
export function dayKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** The local day containing `d`, as [start, end) instants. */
export function dayBounds(d: Date): { from: Date; to: Date } {
  const from = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return { from, to: new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1) };
}

/** First and last+1 day the month grid shows (whole Monday-Sunday weeks). */
export function visibleRange(month: Date): { from: Date; to: Date } {
  const first = startOfMonth(month);
  const lead = (first.getDay() + 6) % 7; // Monday = 0
  const from = new Date(first.getFullYear(), first.getMonth(), 1 - lead);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const trail = 6 - ((last.getDay() + 6) % 7);
  const to = new Date(last.getFullYear(), last.getMonth(), last.getDate() + trail + 1);
  return { from, to };
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_CHIPS = 3;

export function CalendarView({
  month,
  onMonthChange,
  events,
  selectedDay,
  onSelectDay,
  loading = false,
  itemNoun = 'item',
}: {
  /** Any date in the month to show. */
  month: Date;
  onMonthChange: (month: Date) => void;
  events: CalendarEvent[];
  /** `dayKey` of the selected day. */
  selectedDay: string;
  onSelectDay: (day: Date) => void;
  loading?: boolean;
  /** For accessible labels, e.g. "follow-up" -> "3 follow-ups". */
  itemNoun?: string;
}) {
  const byDay = new Map<string, CalendarEvent[]>();
  for (const e of [...events].sort((a, b) => a.start.localeCompare(b.start))) {
    const key = dayKey(new Date(e.start));
    byDay.set(key, [...(byDay.get(key) ?? []), e]);
  }

  const { from, to } = visibleRange(month);
  const days: Date[] = [];
  for (let d = from; d < to; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    days.push(d);
  }
  const today = dayKey(new Date());
  const monthLabel = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className="ne-cal" aria-busy={loading}>
      <div className="ne-cal__header">
        <Button
          variant="secondary"
          aria-label="Previous month"
          onClick={() => onMonthChange(addMonths(month, -1))}
        >
          ‹
        </Button>
        <h2 className="ne-cal__title" aria-live="polite">
          {monthLabel}
        </h2>
        <Button
          variant="secondary"
          aria-label="Next month"
          onClick={() => onMonthChange(addMonths(month, 1))}
        >
          ›
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            const now = new Date();
            onMonthChange(startOfMonth(now));
            onSelectDay(now);
          }}
        >
          Today
        </Button>
      </div>

      <div className="ne-cal__grid" role="grid" aria-label={monthLabel}>
        <div className="ne-cal__row" role="row">
          {WEEKDAYS.map((w) => (
            <div key={w} className="ne-cal__weekday" role="columnheader">
              {w}
            </div>
          ))}
        </div>
        {Array.from({ length: days.length / 7 }, (_, week) => (
          <div key={week} className="ne-cal__row" role="row">
            {days.slice(week * 7, week * 7 + 7).map((d) => {
              const key = dayKey(d);
              const items = byDay.get(key) ?? [];
              const outside = d.getMonth() !== month.getMonth();
              const label = d.toLocaleDateString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              });
              const classes = [
                'ne-cal__day',
                outside ? 'ne-cal__day--outside' : '',
                key === today ? 'ne-cal__day--today' : '',
                key === selectedDay ? 'ne-cal__day--selected' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <div key={key} role="gridcell" aria-selected={key === selectedDay}>
                  <button
                    type="button"
                    className={classes}
                    aria-label={`${label}, ${items.length} ${itemNoun}${items.length === 1 ? '' : 's'}`}
                    aria-current={key === today ? 'date' : undefined}
                    onClick={() => onSelectDay(d)}
                  >
                    <span className="ne-cal__num">{d.getDate()}</span>
                    {items.length > 0 ? (
                      <span className="ne-cal__count" aria-hidden="true">
                        {items.length}
                      </span>
                    ) : null}
                    <span className="ne-cal__chips" aria-hidden="true">
                      {items.slice(0, MAX_CHIPS).map((e) => (
                        <span
                          key={e.id}
                          className={`ne-cal__chip ne-cal__chip--${e.tone ?? 'default'}`}
                        >
                          {e.label}
                        </span>
                      ))}
                      {items.length > MAX_CHIPS ? (
                        <span className="ne-cal__more">+{items.length - MAX_CHIPS} more</span>
                      ) : null}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
